"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  TED_FIELDS as F,
  formatTedTalk,
  type TedKind,
  type TedLabels,
  tedIsReady,
} from "@/lib/tool-kit/citation-sources-more/ted-talk-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const TED_TALK_CITATION_GENERATOR_STRINGS = {
  formHeading: "Talk details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the talk's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "Where did you watch it?",
  kindTed: "On TED.com",
  kindYoutube: "On YouTube",
  speakersLabel: "Speaker(s)",
  speakersPlaceholder: "Vaitea Cowan",
  speakersHint: "One per line, as First Last or Last, First.",
  titleLabel: "Talk title",
  titlePlaceholder: "How green hydrogen could end the fossil fuel era",
  titleHint:
    "Type it in sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  youtubeTitleLabel: "Video title as YouTube shows it (APA)",
  youtubeTitlePlaceholder: "Listening to shame | Brené Brown",
  youtubeTitleHint:
    "APA titles the entry exactly as the channel does, often with the speaker's name added. Leave blank to use the talk title.",
  channelLabel: "Channel that uploaded the video",
  channelPlaceholder: "TED",
  channelHint:
    "TED, TEDx Talks or TED-Ed. APA names this channel as the author of a YouTube copy.",
  tedxLabel: "TEDx event (leave blank for a TED talk)",
  tedxPlaceholder: "TEDxPoughkeepsie",
  locationLabel: "Where it was filmed (Chicago)",
  locationPlaceholder: "Vancouver, BC",
  filmedLabel: "Date filmed",
  postedLabel: "Date posted online (optional)",
  datePlaceholder: "YYYY-MM-DD",
  filmedHint:
    "TED.com shows the month and year, as in 2022-04. YouTube shows the exact day it was posted.",
  lengthLabel: "Running time (Chicago)",
  lengthPlaceholder: "9:15",
  lengthHint: "Minutes and seconds, as shown on the player.",
  timeStampLabel: "Time stamp of a quotation (optional)",
  timeStampPlaceholder: "6:09",
  timeStampHint:
    "Where the passage you quote or paraphrase starts. It goes into the in-text citations.",
  shortTitleLabel: "Short title for notes (Chicago, optional)",
  shortTitlePlaceholder: "Green Hydrogen",
  shortTitleHint:
    "Chicago shortens a long title to its key words, by hand. Leave blank for a suggestion.",
  urlLabel: "URL of the talk",
  urlPlaceholder:
    "https://www.ted.com/talks/vaitea_cowan_how_green_hydrogen_could_end_the_fossil_fuel_era",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 makes the speaker the author of a talk on TED.com, TED Conferences the publisher, and dates it by year and month, with [Video] after the italic title. When you quote, add the time stamp where the quotation starts.",
  apaYoutubeNote:
    "On YouTube, APA 7 names the channel that uploaded the video as the author, to help readers find it, and dates it to the day. Name the speaker in your sentence and cite the channel.",
  mlaNote:
    "The MLA Handbook has no TED example. This applies its core elements to a web video: the talk in quotation marks, the site as the container in italics, the date and the URL. A YouTube copy adds who uploaded it.",
  chicagoNote:
    "Follows the CMOS 18 Quick Guide's TED sample (§14.167): TED Talk, the place and month, and the running time. A TEDx talk uses the event's name in place of TED Talk — a library convention, not a Chicago example. The YouTube form follows the Quick Guide's YouTube sample.",
  exampleTed: {
    speakers: "Vaitea Cowan",
    title: "How green hydrogen could end the fossil fuel era",
    location: "Vancouver, BC",
    filmed: "2022-04",
    length: "9:15",
    shortTitle: "Green Hydrogen",
    url: "https://www.ted.com/talks/vaitea_cowan_how_green_hydrogen_could_end_the_fossil_fuel_era",
  },
  exampleYoutube: {
    speakers: "Brené Brown",
    title: "Listening to shame",
    youtubeTitle: "Listening to shame | Brené Brown",
    channel: "TED",
    location: "Long Beach, CA",
    filmed: "2012-03",
    posted: "2012-03-16",
    url: "https://www.youtube.com/watch?v=psN1DORYYV0",
  },
} as const;

type Strings = typeof TED_TALK_CITATION_GENERATOR_STRINGS;

function sharedFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.speakers,
      kind: "people",
      label: strings.speakersLabel,
      placeholder: strings.speakersPlaceholder,
      hint: strings.speakersHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
  ];
}

function detailFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.tedx,
      kind: "text",
      label: strings.tedxLabel,
      placeholder: strings.tedxPlaceholder,
    },
    {
      id: F.location,
      kind: "text",
      label: strings.locationLabel,
      placeholder: strings.locationPlaceholder,
    },
    {
      id: F.filmed,
      kind: "date",
      label: strings.filmedLabel,
      placeholder: strings.datePlaceholder,
      hint: strings.filmedHint,
    },
    {
      id: F.posted,
      kind: "date",
      label: strings.postedLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.length,
      kind: "text",
      label: strings.lengthLabel,
      placeholder: strings.lengthPlaceholder,
      hint: strings.lengthHint,
    },
    {
      id: F.timeStamp,
      kind: "text",
      label: strings.timeStampLabel,
      placeholder: strings.timeStampPlaceholder,
      hint: strings.timeStampHint,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
      hint: strings.shortTitleHint,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      wide: true,
    },
  ];
}

function buildFields(strings: Strings, kind: TedKind): SourceField[] {
  const youtube: SourceField[] =
    kind === "youtube"
      ? [
          {
            id: F.youtubeTitle,
            kind: "text",
            label: strings.youtubeTitleLabel,
            placeholder: strings.youtubeTitlePlaceholder,
            hint: strings.youtubeTitleHint,
          },
          {
            id: F.channel,
            kind: "text",
            label: strings.channelLabel,
            placeholder: strings.channelPlaceholder,
            hint: strings.channelHint,
          },
        ]
      : [];
  return [...sharedFields(strings), ...youtube, ...detailFields(strings)];
}

/** The widget's strings, as the formatter's labels. */
export function tedLabels(strings: Strings): TedLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    apaYoutubeNote: strings.apaYoutubeNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function TedTalkCitationGeneratorWidget() {
  const strings = useToolUi(TED_TALK_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<TedKind>("ted");
  const fields = useMemo(() => buildFields(strings, kind), [strings, kind]);
  const labels = useMemo(() => tedLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatTedTalk(kind, values, labels),
    [kind, labels]
  );

  return (
    <SourceCitationBuilder
      example={kind === "ted" ? strings.exampleTed : strings.exampleYoutube}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="ted-kind"
          onChange={setKind}
          options={[
            { value: "ted", label: strings.kindTed },
            { value: "youtube", label: strings.kindYoutube },
          ]}
          value={kind}
        />
      }
      isReady={tedIsReady}
      strings={strings}
    />
  );
}
